// The outbox: things the inspector DID that the server has not heard yet.
//
// Photos were durable from day one; nothing else was. Finishing a job went straight
// to the API, so an inspector standing in a valley got "Unable to finish this
// inspection" and had to remember to come back to it — and the visit date was left
// to the server, so a finish that went through the next morning stamped the wrong
// day on the inspection, which then feeds File Life. Notes typed on site would have
// been lost the same way.
//
// So both are queued here and sent with the photo queue. Two kinds today:
//   finish — the decision to finish, with the date FROZEN as the day it was tapped
//   notes  — what the inspector wants the office to read
//
// Keyed per kind + job, so doing the same thing twice REPLACES rather than stacks.
// What is still in the photo queue is deliberately not frozen: it is read again at
// send time, because the photos drain first and the office wants to know what is
// missing NOW, not what was missing an hour ago.

import { finishInspectionJob, saveInspectorNotes } from "./inspectionApi.js";
import { deleteAction, listActions, listUploads, putAction } from "../storage/inspectionStore.js";

const FINISH = "finish";
const NOTES = "notes";
const actionKey = (kind, userId, inspectionId) => `${kind}:${userId}:${inspectionId}`;
const finishKey = (userId, inspectionId) => actionKey(FINISH, userId, inspectionId);
const notesKey = (userId, inspectionId) => actionKey(NOTES, userId, inspectionId);

export async function queueFinish({ userId, inspectionId, dateInspected }) {
  return putAction({
    id: finishKey(userId, inspectionId),
    kind: FINISH,
    userId: Number(userId),
    inspectionId: Number(inspectionId),
    dateInspected,
    createdAt: new Date().toISOString(),
    attempts: 0,
    error: "",
  });
}

// Notes are held under the same key per job, so the newest text replaces the last —
// what the office should read is what the inspector last wrote, not a history of
// keystrokes.
export async function queueNotes({ userId, inspectionId, notes }) {
  return putAction({
    id: notesKey(userId, inspectionId),
    kind: NOTES,
    userId: Number(userId),
    inspectionId: Number(inspectionId),
    notes,
    createdAt: new Date().toISOString(),
    attempts: 0,
    error: "",
  });
}

const ofKind = async (userId, kind) =>
  (await listActions(userId)).filter((row) => row.kind === kind);

export async function listQueuedFinishes(userId) {
  return ofKind(userId, FINISH);
}

export async function queuedFinishFor(userId, inspectionId) {
  const rows = await listQueuedFinishes(userId);
  return rows.find((row) => Number(row.inspectionId) === Number(inspectionId)) || null;
}

export async function queuedNotesFor(userId, inspectionId) {
  const rows = await ofKind(userId, NOTES);
  return rows.find((row) => Number(row.inspectionId) === Number(inspectionId)) || null;
}

// Reopening a job whose finish never left the device: drop the intent rather than
// letting it fire later and re-finish something the inspector deliberately reopened.
export async function discardQueuedFinish(userId, inspectionId) {
  await deleteAction(finishKey(userId, inspectionId));
}

// A note that reached the server directly makes any queued copy stale — without
// this, the next flush would post yesterday's text back over today's.
export async function discardQueuedNotes(userId, inspectionId) {
  await deleteAction(notesKey(userId, inspectionId));
}

function errorMessage(error) {
  if (!navigator.onLine) return "Waiting for a connection.";
  if (error.response?.status === 401) return "Your session has expired. Sign in again.";
  return error.response?.data?.error || error.message || "Not sent yet — it is safe on this device.";
}

// One action. Notes go first when both are queued for the same job (see flushOutbox),
// so the office never reads a finished job whose note is still on the phone.
async function sendAction(userId, action) {
  if (action.kind === NOTES) {
    await saveInspectorNotes(action.inspectionId, action.notes);
    return;
  }
  const stillQueued = await listUploads(userId, action.inspectionId);
  await finishInspectionJob(action.inspectionId, {
    dateInspected: action.dateInspected,
    pendingUploadIds: stillQueued.map((row) => row.id),
  });
}

export async function flushOutbox(userId) {
  let sent = 0;
  // Notes before finishes: a note is what the office reads when deciding whether to
  // accept, so it must not arrive after the thing it explains.
  const actions = (await listActions(userId))
    .sort((a, b) => (a.kind === NOTES ? -1 : 0) - (b.kind === NOTES ? -1 : 0));
  for (const action of actions) {
    if (!navigator.onLine) break;
    try {
      await sendAction(userId, action);
      await deleteAction(action.id);
      sent += 1;
    } catch (error) {
      const status = error.response?.status;
      // A 409 on a FINISH means the office (or this inspector on another device) has
      // already settled it: the intention is satisfied, so stop carrying it.
      // A 409 on NOTES is the opposite — the office accepted while the note sat on
      // the phone, so the note can never land. It is KEPT with the server's message
      // showing on the job page, because quietly dropping what someone wrote is the
      // one thing this queue exists to prevent. Re-sending on each resume costs one
      // request and keeps the message honest until the inspector deals with it.
      if (status === 409 && action.kind === FINISH) {
        await deleteAction(action.id);
        continue;
      }
      // Everything else is KEPT — including a 401, which only means "sign in
      // again", and a 404, which the inspector needs to be told about rather
      // than have quietly dropped. Nothing here is ever discarded silently.
      await putAction({ ...action, attempts: (action.attempts || 0) + 1, error: errorMessage(error) });
      if (status === 401 || status === 403) break; // the rest would fail the same way
    }
  }
  return { sent };
}
